import { Alert, Button } from '@packages/ui/core';
import { Link } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { confirmPasswordReset } from 'firebase/auth';
import { useMemo, useState } from 'react';
import { ValidatedForm, useIsSubmitting, useIsValid } from 'remix-validated-form';
import { z } from 'zod';
import { FirebaseManager } from '~/firebase-manager';
import { getFirebaseAuthErrorMessage } from '~/shared/utils/get-firebase-auth-error-message';
import { MIN_PASSWORD_LENGTH, createPasswordStrengthRefinement } from '~/utils/password-strength';
import { PasswordStrengthInput } from './password-strength-input';

const BaseResetPasswordSchema = z.object({
  password: z.string().min(MIN_PASSWORD_LENGTH, { message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` }),
});

type UserInfo = {
  userId: string;
  email: string;
  firstname: string;
  lastname: string;
};

const createEnhancedResetPasswordSchema = (userInfo?: UserInfo) => {
  return BaseResetPasswordSchema.superRefine(
    createPasswordStrengthRefinement<z.infer<typeof BaseResetPasswordSchema>>({
      userInputsResolver: () => [userInfo?.email, userInfo?.firstname, userInfo?.lastname],
    }),
  );
};

export const createResetPasswordFormValidator = (userInfo?: UserInfo) => withZod(createEnhancedResetPasswordSchema(userInfo));

export type ResetPasswordFormProps = {
  oobCode: {
    value: string;
    invalid: boolean;
  };
  userInfo?: UserInfo;
  success?: boolean;
  error?: string;
  signInUrl?: string;
  validator?: ReturnType<typeof createResetPasswordFormValidator>;
  onSubmit: (data: { password: string }) => void;
};

export type ResetPasswordFormData = {
  password: string;
};

export function useResetPasswordFormSubmit() {
  const [success, setSuccess] = useState<boolean>();
  const [error, setError] = useState<string>();

  return {
    onSubmit: async (oobCode: string, data: ResetPasswordFormData) => {
      const client = FirebaseManager.getApp();

      setSuccess(false);
      setError(undefined);

      try {
        await confirmPasswordReset(client.auth, oobCode, data.password);
        setSuccess(true);
      } catch (err: any) {
        setError(getFirebaseAuthErrorMessage(err.code));
      }
    },
    success,
    error,
  };
}

export const ResetPasswordForm = ({
  oobCode,
  userInfo,
  success = false,
  error,
  signInUrl = '/sign-in',
  validator,
  onSubmit,
}: ResetPasswordFormProps) => {
  const resetPasswordFormValidator = useMemo(() => validator ?? createResetPasswordFormValidator(userInfo), [validator, userInfo]);

  const userInputs = useMemo(() => {
    if (!userInfo) {
      return [];
    }

    const userInputs = [userInfo.email, userInfo.firstname, userInfo.lastname]
      .map(value => value?.trim().toLowerCase())
      .filter((value): value is string => Boolean(value));

    return userInputs;
  }, [userInfo]);

  const isValid = useIsValid('reset-password');
  const isSubmitting = useIsSubmitting('reset-password');

  const disabled = (!error && isSubmitting) || !isValid;
  const isLoading = !error && isSubmitting;

  if (success) {
    return (
      <div className="space-y-4">
        <Alert.Root color="success">
          <Alert.Title>All good!</Alert.Title>
          <Alert.Description>Your password has been reset! You can now sign in with your new password.</Alert.Description>
        </Alert.Root>

        <Link to={signInUrl} className="block">
          <Button isFullWidth>Back to Sign In</Button>
        </Link>
      </div>
    );
  }

  if (oobCode.invalid) {
    return (
      <div className="space-y-4">
        <Alert.Root color="danger">
          <Alert.Title>Something went wrong!</Alert.Title>
          <Alert.Description>
            The password reset link is invalid or has expired. Please request a new password reset link and try again.
          </Alert.Description>
        </Alert.Root>

        <Link to={signInUrl} className="block">
          <Button isFullWidth>Back to Sign In</Button>
        </Link>
      </div>
    );
  }

  return (
    <ValidatedForm
      id="reset-password"
      validator={resetPasswordFormValidator}
      onSubmit={(data, event) => {
        event.preventDefault();
        onSubmit(data);
      }}
      method="POST"
    >
      <div className="space-y-6">
        <div className="space-y-4">
          {error && (
            <Alert.Root color="danger">
              <Alert.Title>Something went wrong!</Alert.Title>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Root>
          )}

          <PasswordStrengthInput name="password" label="New Password" formId="reset-password" userInputs={userInputs} />
        </div>

        <Button type="submit" size="lg" disabled={disabled} isLoading={isLoading} isFullWidth>
          Confirm
        </Button>
      </div>
    </ValidatedForm>
  );
};
